import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import { useNavigate } from 'react-router';
import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import { App, Grid } from 'antd';

import { mutations } from '../../graphql/graphql';
import { IBaseFilter, IPlan } from '../../types/plans';
import { IMapController, IMapData } from '../../lib/map';

import { uploadImage } from '../../utils/utils';
import MobileMapView from './MobileMapView';
import DesktopMapView from './DesktopMapView';
import { mapSession } from '../../graphql/queries/queries';
import OnboardingModal from '../../components/UI/OnboardingModal';
import { mapListOnboarding } from './constants/onboarding';
import { MapPhaseData } from '../../modules/project/models/MapPhaseModel';

const { useBreakpoint } = Grid;

export type MapListNextPayload = {
  mapResultId?: string;
  address?: string;
  planId?: string;
  coordinates?: [number, number];
  rotation?: number;
};

interface MapListProps {
  prefix?: string;
  queries: DocumentNode;
  onNext?: (data: MapListNextPayload) => void | Promise<void>;
  initialPhaseData?: MapPhaseData;
}

export const MapList: React.FC<MapListProps> = ({ prefix, queries, onNext, initialPhaseData }) => {
  const { message } = App.useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const refController = useRef<IMapController | null>(null);
  const [filters, setFilters] = useState<IBaseFilter>({});
  const [planView, setPlanView] = useState<IPlan | null>(null);
  const [mapData, setMapData] = useState<IMapData | null>();
  const [mapResult, setMapResult] = useState<{ id?: string } | null>(() => {
    if (!initialPhaseData?.mapResultId) return null;
    return { id: initialPhaseData.mapResultId };
  });
  const [loadMap, setLoadMap] = useState(false);
  const [open, setOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const pageSize = 10;

  const [MAP_RESULT_CREATE] = useMutation(mutations.map.MAP_RESULT_CREATE);
  const { data, loading } = useQuery(queries, {
    variables: { ...filters, page: currentPage, pageSize },
    fetchPolicy: 'network-only',
  });
  const { data: mapSessionData, loading: mapSessionLoading } = useQuery(
    mapSession.GET_MAP_SESSION,
    {
      fetchPolicy: 'network-only',
    }
  );
  const [MAP_SESSION_UPDATE] = useMutation(mutations.mapSession.UPDATE_MAP_SESSION);

  const items = useMemo(() => {
    const plansData = data && data[Object.keys(data)[0]];
    return plansData?.items || [];
  }, [data]);

  const totalCount = useMemo(() => {
    const plansData = data && data[Object.keys(data)[0]];
    return plansData?.totalCount || 0;
  }, [data]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const buildMapPayload = (data?: IMapData | null) => {
    if (!data) return undefined;

    const coordinates =
      typeof data.center?.toLngLat === 'function' ? data.center.toLngLat() : undefined;
    const rotation = data.rotation;

    return {
      ...data,
      center: data.center ? { x: data.center.x, y: data.center.y } : undefined,
      rotation,
      coordinates,
    };
  };

  const onView = async () => {
    if (loadMap) {
      return null;
    }
    if (!planView?.id) {
      message.error('Please select a plan');
      return null;
    }
    if (!mapData?.address) {
      message.error('Please enter your address');
      return null;
    }
    if (!refController.current) {
      message.error('Map is not ready yet');
      return null;
    }

    setLoadMap(true);
    const shouldNavigate = Boolean(prefix) && !onNext;
    try {
      refController.current.clearRotateLayer();
      const file = await refController.current.takeScreenshot();
      refController.current.createRotateLayer();
      const snapshotLink = await uploadImage(file, 'map-images');
      const mapPayload = buildMapPayload(mapData);
      const data = {
        snapshot: snapshotLink.original.url,
        compressedSnapshot: snapshotLink.compressed.url,
        map: mapPayload,
        plan: planView.id,
        user: user?.id,
      };

      const res = await MAP_RESULT_CREATE({
        variables: data,
      });
      message.success('Successfully saved');
      const mapResultCreate = res.data?.mapResultCreate;
      setMapResult(mapResultCreate);

      if (shouldNavigate) {
        navigate(`/${prefix}/${planView?.id}/${mapResultCreate.id}`);
      }

      return {
        mapResultId: mapResultCreate?.id,
        address: mapPayload?.address ?? mapData?.address,
        planId: planView.id,
        coordinates: mapPayload?.coordinates,
        rotation: mapPayload?.rotation,
      };
    } catch (error) {
      console.error('[MapList] create map result failed', error);
      message.error('Failed to create result');
      if (shouldNavigate) {
        navigate(`/${prefix}/${planView?.id}`);
      }
      return null;
    } finally {
      setLoadMap(false);
    }
  };

  useEffect(() => {
    const initialAddress =
      initialPhaseData?.address || mapSessionData?.mapSession?.mapData?.address;
    if (!!initialAddress && refController.current) {
      refController.current.setGeocoderAddress(initialAddress);
    }
    if (mapSessionData?.mapSession && !mapSessionLoading) {
      const hasCompletedOnboarding = mapSessionData.mapSession.hasCompletedOnboarding;
      if (!hasCompletedOnboarding) {
        setShowOnboarding(true);
      }
    }
    return () => {
      if (refController.current) {
        refController.current.clearGeocoderAddress();
        refController.current.clear();
      }
    };
  }, [mapSessionData, mapSessionLoading, initialPhaseData?.address]);

  useEffect(() => {
    if (!initialPhaseData?.planId) return;
    if (planView?.id) return;
    const matchingPlan = items.find((plan: IPlan) => plan.id === initialPhaseData.planId);
    if (matchingPlan) {
      setPlanView(matchingPlan);
    }
  }, [initialPhaseData?.planId, items, planView?.id]);

  const onPlace = (e: React.MouseEvent<HTMLDivElement>, plan: IPlan) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mapData?.address) {
      message.error('Please enter your address');
      setOpen(false);
      return;
    }
    if (isMobile) {
      setOpen(false);
    }
    setPlanView(plan);
    MAP_SESSION_UPDATE({
      variables: {
        input: {
          mapData,
          lastViewed: plan.id,
        },
      },
    });
  };

  const onChangeMapData = (data: IMapData) => {
    setMapData(prev => {
      if (isMobile && data.address && !prev?.address) {
        setOpen(true);
      }
      if (data.city) {
        setFilters(prev => ({ ...prev, city: data.city }));
      }
      if (data.county) {
        setFilters(prev => ({ ...prev, county: data.county }));
      }
      if (data.state) {
        setFilters(prev => ({ ...prev, state: data.state }));
      }
      return data;
    });
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleOnboardingComplete = () => {
    MAP_SESSION_UPDATE({
      variables: {
        input: {
          hasCompletedOnboarding: true,
        },
      },
    });
    setShowOnboarding(false);
  };

  const primaryLabel = onNext ? 'Next' : 'Estimate';
  const handlePrimaryAction = onNext
    ? async () => {
        const payload = await onView();
        if (!payload) return;
        await onNext(payload);
      }
    : onView;

  return (
    <>
      {isMobile ? (
        <MobileMapView
          refController={refController}
          planView={planView}
          onChangeMapData={onChangeMapData}
          setLoadMap={setLoadMap}
          loadMap={loadMap}
          filters={filters}
          setFilters={setFilters}
          loading={loading || mapSessionLoading}
          items={items}
          onPlace={onPlace}
          onNext={handlePrimaryAction}
          primaryLabel={primaryLabel}
          setOpen={setOpen}
          open={open}
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalCount={totalCount}
        />
      ) : (
        <DesktopMapView
          refController={refController}
          planView={planView}
          onChangeMapData={onChangeMapData}
          setLoadMap={setLoadMap}
          loadMap={loadMap}
          filters={filters}
          setFilters={setFilters}
          loading={loading || mapSessionLoading}
          items={items}
          onPlace={onPlace}
          onNext={handlePrimaryAction}
          primaryLabel={primaryLabel}
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalCount={totalCount}
        />
      )}

      <OnboardingModal
        open={showOnboarding}
        onClose={handleOnboardingComplete}
        image={mapListOnboarding.image}
        title={mapListOnboarding.title}
        badge={mapListOnboarding.badge}
        buttonText={mapListOnboarding.buttonText}
        content={mapListOnboarding.content}
        onButtonClick={handleOnboardingComplete}
      />
    </>
  );
};

export default MapList;
